import React, { useEffect, useRef } from 'react';

const eventTypes = [
  'pointerdown',
  'pointerup',
  'pointermove',
  'pointerenter',
  'pointerleave',
  'pointerover',
  'pointerout',
  'pointercancel',
  'gotpointercapture',
  'lostpointercapture',
  'mousedown',
  'mouseup',
  'mousemove',
  'mouseenter',
  'mouseleave',
  'mouseover',
  'mouseout',
  'click',
  'dblclick',
  'contextmenu',
  'wheel',
];

const handlers = new Map() as Map<
  (typeof eventTypes)[number],
  (e: Event) => void
>;

/**
 * Dispatches all MouseEvents and PointerEvents to Viewer preview frame.
 * Used for Mouse Parallax animation preview mode (santa-editor/packages/animations/api/v3/previewApi.ts).
 * In Classic Editor, Viewer iframe has z-index:0 in Edit mode which puts it below the Editor's overlay, so events don't reach it. Because of this manual dispatching is needed.
 */
export const RetriggerMouseEventsToPreview = () => {
  const divRef = useRef<HTMLDivElement>();

  useEffect(() => {
    const previewDocument = (
      document.getElementById('preview') as HTMLIFrameElement
    )?.contentDocument;
    const currentDiv = divRef.current; // needed to preserve the reference for the cleanup function

    if (!previewDocument) {
      return;
    }

    eventTypes.forEach((eventType) => {
      const eventHandler = (event: Event) => {
        const EventConstructor = event.constructor as {
          new (type: string, eventInitDict?: EventInit): Event;
        };
        const clonedEvent = new EventConstructor(event.type, event);
        previewDocument.dispatchEvent(clonedEvent);
      };

      handlers.set(eventType, eventHandler);
      currentDiv.addEventListener(eventType, eventHandler);
    });

    return () => {
      Object.entries(handlers).forEach(([eventType, handler]) => {
        currentDiv.removeEventListener(eventType, handler);
        handlers.delete(eventType);
      });
    };
  }, []);

  return (
    <div
      ref={divRef}
      style={{
        position: 'absolute',
        height: '100%',
        width: '100%',
      }}
    />
  );
};
